import ClickablePseudo from '../ClickablePseudo';
import { useState } from 'react';
import type { LibrarySharePayload } from '../../types/chat';
import {
  LIBRARY_SHARE_CONTRACT_LABELS,
  LIBRARY_SHARE_ORIGIN_LABELS,
} from '../../types/chat';

interface LibraryShareMessageProps {
  share: LibrarySharePayload;
  author: string;
  showProposer?: boolean;
  onOpen: () => void;
  canCancel?: boolean;
  onCancel?: () => Promise<void>;
}

function formatContract(contract: number | null): string {
  if (contract == null) return 'Partie';
  return LIBRARY_SHARE_CONTRACT_LABELS[contract] ?? `Contrat ${contract}`;
}

function formatOrigin(originType: string): string {
  return LIBRARY_SHARE_ORIGIN_LABELS[originType] ?? originType;
}

export default function LibraryShareMessage({
  share,
  author,
  showProposer = true,
  onOpen,
  canCancel = false,
  onCancel,
}: LibraryShareMessageProps) {
  const [loading, setLoading] = useState(false);
  const isCancelled = !!share.cancelledAt;
  const contractLabel = formatContract(share.contract);
  const originLabel = formatOrigin(share.originType);

  const handleCancel = async () => {
    if (!onCancel || loading) return;
    setLoading(true);
    try {
      await onCancel();
    } finally {
      setLoading(false);
    }
  };

  if (isCancelled) {
    return (
      <div className="game-invite-card status-cancelled game-invite-card--compact library-share-card">
        <div className="game-invite-header">
          <span className="game-invite-icon" aria-hidden="true">📚</span>
          <div className="game-invite-title-wrap">
            <span className="game-invite-title">
              {share.isPoll ? 'Sondage annulé' : 'Partage annulé'}
            </span>
            <span className="game-invite-subtitle">
              {contractLabel} · {originLabel}
            </span>
          </div>
        </div>

        {showProposer && (
          <div className="game-invite-author">
            Partagé par <ClickablePseudo pseudo={author}>{author}</ClickablePseudo>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="game-invite-card library-share-card">
      <div className="game-invite-header">
        <span className="game-invite-icon" aria-hidden="true">📚</span>
        <div className="game-invite-title-wrap">
          <span className="game-invite-title">
            {share.isPoll ? 'Sondage partagé' : 'Partie partagée'}
          </span>
          <span className="game-invite-subtitle">
            {contractLabel} · {originLabel}
          </span>
        </div>
      </div>

      {showProposer && (
        <div className="game-invite-author">
          Partagé par <ClickablePseudo pseudo={author}>{author}</ClickablePseudo>
        </div>
      )}

      {share.shareComment && (
        <p className="library-share-comment">{share.shareComment}</p>
      )}

      <div className="game-invite-actions">
        <button
          type="button"
          className="game-invite-btn access"
          disabled={loading}
          onClick={onOpen}
        >
          {share.isPoll ? 'Répondre au sondage' : 'Voir la partie'}
        </button>
        {canCancel && onCancel && (
          <button
            type="button"
            className="game-invite-btn cancel"
            disabled={loading}
            onClick={() => { void handleCancel(); }}
          >
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}
